//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------


import { find, get, has, map } from "lodash";
import React, { useCallback, useMemo, useState, useEffect} from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import { 
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography
} from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import { ArrowRight, Check, Close } from '@mui/icons-material';
//  -- Constants ----------------------
import COLOR from "utils/constants/color";

//  -- Lib ----------------------------
import { getStatusByCode, imageFetch, imageFetchFromPodcast, isVideo, subMissionViewTBG } from "utils/lib/ebookHelper";

//  -- Thunks -------------------------
import GradeDisplay from 'components/GradeDisplay';
import "moment-timezone";
import { useOrgTimezone } from 'store/organizations/selectors';
import { useResources } from "store/resources/selectors";
import { useServertime } from 'store/servertime/selectors';
import { BookSynoname } from 'utils/config/reference';
import { checkIsManualGradable, disableBodyScroll, isAfterTime, isGradedByTeacher } from "utils/methods/math";

import { Col, Row } from "antd";
import { lighten } from "polished";
import { useAllBook } from 'store/books/selectors';
import { useCurrentUser } from "store/users/selectors";
import { debatePoster } from "utils/assets/images";
import AssignmentImgModal from 'features/AssignmentImgModal';
import { usePageCover } from "components/hooks/pageCover/pageCover";
import { useGradeBook } from "components/hooks/gradeBook/useGradeBook";


//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------



const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
`;


const Header = styled.span`
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  @media (max-width: 968px) {
    padding-left:5px;
  }
`;

const Image = styled.img`
  border: 1px solid ${COLOR.black800};
  width: 100%;
  height: auto;
`;

const ImageContiner = styled.button`
  background-color: transparent;
  border: none;
  cursor: zoom-in;
  flex: 1;
  max-width: 112px;
  outline: none;
  padding-right: 24px;
`;



const Wrapper = styled.div`
  align-items: flex-start;
  display: flex;
  width: 100%;
  height: auto;
  padding: 16px;
  border-radius: 3px;
  margin-bottom: 16px;
  position: relative;

  @media (max-width: 400px) {
    padding-bottom: 72px;
  }
  @media (max-width: 500px) {
    padding: 8px!important;
 }
`;
const ResponceContainer = styled.div`
  min-height: 50px;
  padding-left:16px;
  padding-right:16px;

  @media screen and (max-width: 992px) {
     padding-left:0px;
     padding-right:0px
  }
`;
const OuterWrapper = styled.div`
//   border-bottom: 2px solid #aaacaf61;
`
const AccordianTitle = styled(Typography)`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
`;
const Rotate = styled.div`
    align-items: center;
    display: flex;
    height: 16px;
    justify-content: center;
    margin-right: 4px;
    transform: ${p => p.open ? 'rotate(90deg)' : 'rotate(0deg)'};
    transition: all 300ms ease;
    width: 16px;
`;
const Notification = styled.div`
    background-color: ${p => p.bgColor ? p.bgColor : lighten(0.45, '#3D5F8F')};
    border: 1px dashed ${p => p.bgColor ? p.color : '#3D5F8F'};
    border-radius: 3px;
    color: ${p => p.color ? p.color : '#3D5F8F'};
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 24px;
    padding: 8px;
`;

const StyledAccordion = muiStyled(Accordion)(({ theme }) => ({
  border: '1px solid white',
  boxShadow: 'none',
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 'auto',
  },
}));

const StyledAccordionSummary = muiStyled(AccordionSummary)(({ theme }) => ({
  backgroundColor: '#03989e',
  color: 'white',
  textTransform: 'capitalize',
  borderBottom: '1px solid white',
  marginBottom: -1,
  minHeight: 30,
  height: 30,
  '&.Mui-expanded': {
    minHeight: 30,
  },
  '& .MuiAccordionSummary-content': {
    '&.Mui-expanded': {
      margin: '8px 0',
    },
  },
}));

const StyledAccordionDetails = muiStyled(AccordionDetails)(({ theme }) => ({
  padding: '2px',
  display: "flex",
  flexDirection: "column"
}));

const CurrentSubmission = {
    backgroundColor: '#9933ff'
}
//  ----------------------------------------------------------------------------
//  Component
//  ----------------------------------------------------------------------------
//
//  Interaction
//
//  -- Description
//  TODO
//
//  -- Props
//  ...
//
//  -- State
//  ...
//
//  -- Methods
//  ...

const StudentSubmissionView = ({
    chapter,
    data,
    submission
}) => {
    const {hasValidAiFeedback} = useGradeBook();
    const servertime = useServertime()
    const timezone = useOrgTimezone()
    const booksAllList = useAllBook();
    const currentUser = useCurrentUser()
    const [modalOpen, setModalOpen] = useState(false);
    const isAssignmentTakable = useCallback(() => {
        if (get(data, "lateDate") == "always") {
            return true;
        }
        else if (get(data, "lateDate")) {
            if (isAfterTime(get(data, "lateDate"), servertime, timezone)) {
                return true;
            } else if (isAfterTime(get(data, "dueDate"), servertime, timezone)) {
                return true;
            }
        } else {
            if (isAfterTime(get(data, "dueDate"), servertime, timezone)) {
                return true;
            }
            return false;
        }
    }, [servertime, data]);
    const pastSubmissions = useMemo(() => {
        if (!submission || !submission.pastSubmissions) return [];
        const returnedAtNumbers = Array.isArray(submission.returned_at)
            ? submission.returned_at.map(num => parseInt(num))
            : []; 
        const grouped = submission.pastSubmissions.reduce((acc, sub) => {
            const submissionNum = parseInt(sub.submissions);
            if (!acc[submissionNum]) {
                acc[submissionNum] = [];
            }
            acc[submissionNum].push(sub);
            return acc;
        }, {});

        const filtered = Object.values(grouped).flatMap((group) => {
            const hasNonEmptyAnswers = group.some(sub => Array.isArray(sub.answers) && sub.answers.length > 0);
            if (hasNonEmptyAnswers&&!returnedAtNumbers.includes(parseInt(group[0].submissions))) {
                return group.filter(sub => Array.isArray(sub.answers) && sub.answers.length > 0);
            }
            return group;
        });
        const sorted = filtered.sort((a, b) => parseInt(a.submissions) - parseInt(b.submissions));
        return sorted;
    }, [submission]);    
    const resources = useResources()
    const ungraded = get(data, "ungraded", false)
    const grade_type = get(data, "grade_type");
    const isCompleteInComplete = grade_type === "complete_incomplete"
    const max_point = data?.max_point && !isNaN(data?.max_point) ? Number.parseInt(data.max_point, 10) : 100
    const isPointSystem = grade_type === "points"
    const is_game = submission?.assignmentType === "game" || submission?.type === "game";
    const is_debate = submission?.assignmentType === "debate" || submission?.type === "debate";
    const is_worksheet = submission?.assignmentType === "worksheet" || submission?.type === "worksheet";
    const is_data_debate = data?.type === "debate" || false;
    const [expanded, setExpanded] = React.useState(get(submission, "id"));
    const assignmentData = useMemo(() => data?.data || data, [data]);
    const pageCover = usePageCover({assignmentData: assignmentData});

    const getNumberMask = useCallback((_page) => {
        let _book = find(booksAllList, { id: chapter })
        if (_book) {
            let numberMask = find(_book.pages, k => k.pageId == _page)
            if (numberMask) {
                if (numberMask.NumberMasked)
                    return numberMask.NumberMasked
                else
                    return _page;
            } else {
                return _page;
            }
        } else {
            return _page;
        }
    }, [chapter, booksAllList])


    useEffect(() => {
        document.documentElement.style.overflow = "hidden";
        disableBodyScroll('StudentSubmissionView');
      
        return () => {
          document.documentElement.style.overflow = "auto";
          document.body.style.overflow = "auto";
        };
      }, []);


    if (data && data.lateDate == "null") {
        data.lateDate = null;
    }

    const getAssignmentName = () => {
        let displayName = data?.displayName ? data?.displayName : data?.data?.displayName
        let shortName = BookSynoname[chapter];
        let shortN = data?.typeId === "quiz" ? "Q" : "A";
        let pg = getNumberMask(data?.data?.page);
        pg = pg?.length > 20 ? data?.data?.bookId?.substring(0, 10) : `Pg.${pg ?? ""}`;
        return is_data_debate ? displayName : `${displayName} (${shortName ?? shortN}|${pg})`
    }
    const getThumbnail = (zoom) => {
        const src = isVideo(get(data, "data.chapter"), resources) ? imageFetchFromPodcast(get(data, "data.page"), resources) : imageFetch(booksAllList, chapter, get(data, "data.page"))
        return <Image
            src={is_data_debate ? debatePoster : src}
            alt=""
        />
    }
    const getCompleteInclompleteMark = (isComplete) => {
        if (isComplete) {
            return <Check style={{ color: COLOR.green100, fontSize: 18 }} />
        } else {
            return <Close style={{ color: COLOR.red, fontSize: 18 }} />
        }
    }

    const getGrade = () => {
        if (!submission || !submission.id) return '-';
        const isWorksheetInProgress = submission?.assignmentType === "worksheet" && !submission?.complete && (submission?.answers?.length || 0) > 0;
        if (isWorksheetInProgress || (submission.retaking && !submission.complete)) {
            return "in progress";
        }
        const grade = submission?.grade == 0 || submission?.grade >0 ? submission.grade : '';
        const aiGradeExists = Array.isArray(submission?.ai_grade) && submission.ai_grade.length > 0;
        let bg_text;
        if (currentUser?.role === 'student') {
          bg_text = 'TBG';
        } else {
          bg_text = aiGradeExists ? "TBG - AI" : "TBG";
        }
        const face_grade = submission?.face_grade;
        const isTbg = submission?.manualGrading && checkIsManualGradable(get(submission, "assignment.questions"))
        if(submission?.assignmentType === "bingo" && ungraded){
            const isSubmitted = submission?.answers?.length > 0 ? submission?.answers?.some((el)=> el?.is_submitted):false;
            return isSubmitted ? 'Credit' : '-';
          }
        if (ungraded) {
            return 'Credit';
        }
        if (isCompleteInComplete && (has(submission, "id") || !isAssignmentTakable())) {
            return getCompleteInclompleteMark(submission?.grade > 0);
        }
        return subMissionViewTBG({isPointSystem, isTbg, face_grade, bg_text, max_point, grade});
    };
    const handleChangeExpend = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : null);
    };
    const getGradePastSubmissions = (pt) => {
        const grade = pt?.grade>0||pt?.grade==0?pt?.grade:0;
        if (ungraded) return 'Credit'
        if (isCompleteInComplete) {
            return grade > 0 ? 'Completed' : 'In completed';
        } else if (isPointSystem) {
            if (grade > 0 && max_point > 0) {
                return `${Math.round((grade / 100) * max_point)}/${max_point}`
            } else {
                return `0/${max_point}`;
            }

        }
        return `Grade -${grade}`;

    }
    const getDisplayName = useCallback(() => {
        if (data) {
            if (data) return data.displayName
            else if (submission && submission.assignment) return submission.assignment.displayName
            return data.data?.displayName || '';
        }
        return ''
    }, [data])
    const pastSubmissionView = useCallback(() => {
        if (pastSubmissions && pastSubmissions.length > 0) {
            return (<div>
                {map(pastSubmissions, (pt, index) => {
                      const isReturned =  (pt?.returned > 0 && pt?.submissions === (pt?.returned - 1))
                    return (
                        <StyledAccordion 
                        expanded={expanded === pt.id} 
                        onChange={handleChangeExpend(pt.id)}
                    >
                        <StyledAccordionSummary aria-controls="panel1d-content" id="panel1d-header">
                            <AccordianTitle>
                                <span>{"Submission-" + (index + 1)} {isReturned ? `(Returned)` : ""} {getGradePastSubmissions(pt)} {isGradedByTeacher(pt,true)}</span> 
                                <Rotate open={expanded === pt.id}><ArrowRight /></Rotate> 
                            </AccordianTitle>
                        </StyledAccordionSummary>
                        <StyledAccordionDetails>
                            <div className="accordionDetailsScroll">
                                <GradeDisplay
                                    key={get(pt, "id")}
                                    answers={get(pt, "answers")}
                                    feedback={get(pt, "feedback")}
                                    ai_feedback={get(pt, "ai_feedback")}
                                    ai_suggestion={get(pt, "ai_suggestion")}
                                    audioFeedback={get(pt, "audioFeedback")}
                                    grade={get(pt, "grade")}
                                    //name={get(submission, "displayName", get(data, "assignment.displayName"))}
                                    name={getDisplayName()}
                                    questions={get(submission, "assignment.questions", [])}
                                    type={"past due"}
                                    isLate={pt.islate}
                                    isExpended
                                    assignment={pt}
                                    activity={data}
                                    isUngraded={data?.ungraded || false}
                                    hasFeedback={true && (get(pt, "feedback") || get(pt, "audioFeedback"))}
                                />
                            </div>
                        </StyledAccordionDetails>
                    </StyledAccordion>

                    )
                }
                  
                )}
            </div>)
        }
        else {
            return null;
        }
    })

    const CurrentSubmissionView = useCallback(() => {
        if (get(submission, "submissions") != 0 || get(submission, "graded") || get(submission, "answers.length") > 0) {
            let PastSub = 0;
            map(pastSubmissions, pt => (
                PastSub = parseInt(pt.submissions)
            ));
            const LastSub = PastSub ? PastSub : 0;
            const CurrentSub = LastSub + 1;
            let Title = "";
            if (CurrentSub === 1) {
                Title = (pastSubmissions?.length + 1);
            } else {
                Title = (pastSubmissions?.length + 1) + ' (Last Submission)';
            }
            const status = getGrade()
            const is_inprogress = status === 'in progress'
            const grade_val = get(submission, "exceptionStatus") > 0 ? getStatusByCode(get(submission, "exceptionStatus"), currentUser) : status
            const isReturned =  (submission?.returned > 0 && submission?.submissions === (submission?.returned - 1));
            const isValidFeedback = hasValidAiFeedback(submission);
            return (<div className="accordiaAssignment">
                {<StyledAccordion square expanded={expanded === get(submission, "id")} onChange={handleChangeExpend(get(submission, "id"))}>
                    <StyledAccordionSummary style={CurrentSubmission} aria-controls="panel1d-content" id="panel1d-header">
                        <AccordianTitle><span>{"Submission-" + Title} {isReturned ? `(Returned)` : ""}  {` Grade - ${!isValidFeedback ? "(Processing)" : grade_val != null ? grade_val : ''}`} {isGradedByTeacher(submission)}</span> <Rotate open={expanded === get(submission, "id")}><ArrowRight /></Rotate> </AccordianTitle>
                    </StyledAccordionSummary>
                    <StyledAccordionDetails>
                    <div className="accordionDetailsScroll">
                        {is_inprogress ? <Notification>The assignment process is currently in progress.</Notification> : null}
                        {currentUser?.role === "student" && is_inprogress ?  null : 
                         <GradeDisplay
                         key={get(submission, "id")}
                         answers={get(submission, "answers")}
                         feedback={get(submission, "feedback")}
                         ai_feedback={get(submission, "ai_feedback")}
                         ai_suggestion={get(submission, "ai_suggestion")}
                         audioFeedback={get(submission, "audioFeedback")}
                         grade={get(submission, "exceptionStatus") > 0 ? getStatusByCode(get(submission, "exceptionStatus"), currentUser) : getGrade()}
                         name={getDisplayName()}
                         questions={get(submission, "assignment.questions", [])}
                         selected_student_id={submission?.userId}
                         type={"current due"}
                         is_game={is_game}
                         is_debate={is_data_debate || is_debate}
                         is_worksheet={is_worksheet}
                         isLate={get(submission, "isLate")}
                         isUngraded={data?.ungraded || false}
                         isExpended
                         activity={data}
                         assignment={submission}
                     />
                        }
                       
                        </div>
                    </StyledAccordionDetails>
                </StyledAccordion>}
            </div>)
        }
    })

    const page_src = useMemo(() => {
        if (!get(data, "data")) return null;
        const assignment_chapter = get(data, "chapter") ? get(data, "chapter") : get(data, "data.chapter")
        const page_id = get(data, "data.page")
        const is_debate = get(submission, "assignmentType", '') === "debate"
        const is_worksheet = get(submission, "assignmentType", '') === "worksheet"
        if(is_worksheet){
            return 
        }
        if (is_debate) {
            return debatePoster
        } else {
            return isVideo(assignment_chapter, resources) ? imageFetchFromPodcast(assignment_chapter, resources) : imageFetch(booksAllList, assignment_chapter, page_id);
        }
    }, [submission, data, resources, booksAllList])

    if ((!data.data || !chapter || !Object.keys(data.data ?? {}).length > 0) && !is_data_debate)
        return null;

    return (
        <OuterWrapper>
            <Wrapper>
              
                <AssignmentImgModal className="max-w-581px" modalOpen={modalOpen} setModalOpen={setModalOpen} page_src={page_src} />
                <Row style={{width:"100vw"}} className=''>
                    <Col span={12} xs={24} sm={24} lg={12} xl={12} >
                    <div className="responsiveCol">
                        <div className='studentSubImgOne' 
                        onClick={() => setModalOpen(true)}
                        style={{ cursor: "pointer" }} >
                          <Image src={pageCover ? pageCover : page_src} alt='' />
                        </div>
                            <Header className="breakTheSentence">
                            {/* {getAssignmentName()} */}
                            {/* {!isNaN(parseInt(data?.timeDuration)) && data?.timeDuration > 0 && (
                                <WatchLater style={{ fontSize: "15px", marginLeft: "6px" }} />
                            )} */}
                        </Header>
                        <div className='studentSubImgTwo'><Image src={pageCover ? pageCover : page_src} alt='' /></div>
                        </div>
                    </Col>
                    <Col span={12} xs={24} sm={24} lg={12} xl={12}>
                        <ResponceContainer>
                            {pastSubmissionView()}
                            {CurrentSubmissionView()}
                        </ResponceContainer>
                    </Col>
                    
                </Row>
                <Container>

                </Container>
            </Wrapper>

        </OuterWrapper>
    );
};

//  -- Prop types ---------------------

const mapStateToProps = (state) => ({
    currentUser: state.currentUser,
});

export default connect(mapStateToProps, null)(StudentSubmissionView);
